import React from "react";
import { useTranslation } from "react-i18next";
import { PieCenterLabel, PieChart } from "@outpave/ds";
import Typography from "@mui/material/Typography";
import { formatCash, formatCashFull } from "../../../utils/formatCash";
import { Center } from "components/layout/reusableLayouts";

export interface SpendSlice {
  id: string;
  label: string;
  value: number;
  color: string;
}

interface PieChartSpendingProps {
  slices: SpendSlice[];
  /** Edge of the square plot, in px. */
  size: number;
  currency: string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  highlightedId: string | null;
  onHighlightChange: (id: string | null) => void;
}

/** Share of the outer radius the donut hole takes. */
const HOLE_RATIO = 0.6;
/** The package leaves this much between the arc and the edge of the plot. */
const PLOT_INSET = 8;
/** Past this the centre total switches to the compact form so it fits the hole. */
const COMPACT_FROM = 999999;

/** Share of spend by group. The legend is the panel's, shared with the bars. */
const PieChartSpending: React.FC<PieChartSpendingProps> = ({
  slices,
  size,
  currency,
  selectedIds,
  onSelectionChange,
  highlightedId,
  onHighlightChange,
}) => {
  const { t } = useTranslation();

  if (slices.length === 0) {
    return (
      <Center sx={{ width: size, height: size }}>
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("No Data Found")}
        </Typography>
      </Center>
    );
  }

  return (
    <PieChart
      height={size}
      legend={false}
      series={[
        {
          data: slices,
          innerRadius: Math.round((size / 2 - PLOT_INSET) * HOLE_RATIO),
          paddingAngle: 2,
          cornerRadius: 5,
          valueFormatter: (item) => formatCashFull(item.value, currency),
        },
      ]}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      highlightedId={highlightedId}
      onHighlightChange={onHighlightChange}
    >
      {(_active, state) => (
        <PieCenterLabel>
          {state.total < COMPACT_FROM
            ? formatCashFull(state.total, currency)
            : formatCash(state.total, currency)}
        </PieCenterLabel>
      )}
    </PieChart>
  );
};

export default PieChartSpending;
